/**
 * AttentionBlock — сводка по разделам, требующим внимания.
 *
 * Объединяет просроченные и истекающие в ближайшие 5 дней разделы.
 * Сортировка: сначала просроченные, затем по сроку.
 * На широких экранах — таблица; на узких (<768px) — карточки в столбик.
 *
 * Props:
 *   filter — 'all' | 'overdue' | 'due_soon' — внешний фильтр из KPI-плашки.
 *   onFilterChange — обратный вызов при смене таба.
 */
import { Link } from 'react-router-dom'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { format, differenceInCalendarDays } from 'date-fns'
import { ru } from 'date-fns/locale'
import clsx from 'clsx'
import type { AttentionItem, SectionStatus } from '@/types'
import { DOC_TYPE_LABELS } from '@/types'

export type AttentionFilter = 'all' | 'overdue' | 'due_soon'

const STATUS_LABEL: Record<SectionStatus, string> = {
  not_started: 'Не начат',
  in_progress: 'В работе',
  on_review: 'На проверке',
  suspended: 'Приостановлен',
  issued: 'Выдан',
  accepted: 'Принят',
}

interface Props {
  items: AttentionItem[]
  filter?: AttentionFilter
  onFilterChange?: (f: AttentionFilter) => void
}

export default function AttentionBlock({ items, filter = 'all', onFilterChange }: Props) {
  const overdueCount  = items.filter((i) => i.is_overdue).length
  const dueSoonCount  = items.filter((i) => !i.is_overdue).length

  const visible = filter === 'all'
    ? items
    : filter === 'overdue'
      ? items.filter((i) => i.is_overdue)
      : items.filter((i) => !i.is_overdue)

  if (items.length === 0) {
    return (
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-5 py-4 flex items-center gap-3">
        <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-emerald-800">Всё в порядке</p>
          <p className="text-xs text-emerald-700">
            Нет просроченных разделов и приближающихся сроков
          </p>
        </div>
      </div>
    )
  }

  const tabs: { key: AttentionFilter; label: string; count: number; activeColor: string }[] = [
    { key: 'all',      label: 'Все',         count: items.length, activeColor: 'border-gray-600 text-gray-900' },
    { key: 'overdue',  label: 'Просрочено',  count: overdueCount, activeColor: 'border-rose-500 text-rose-700' },
    { key: 'due_soon', label: 'Скоро срок',  count: dueSoonCount, activeColor: 'border-amber-500 text-amber-700' },
  ]

  return (
    <div id="attention-block" className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
      {/* Шапка с табами */}
      <div className="flex items-center justify-between px-5 pt-3 border-b border-gray-100">
        <div className="flex items-center gap-2 mb-0">
          <AlertCircle className="h-4 w-4 text-rose-500 flex-shrink-0" />
          <h2 className="text-sm font-semibold text-gray-900">Требуют внимания</h2>
        </div>
        {/* Табы */}
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => onFilterChange?.(tab.key)}
              className={clsx(
                'flex items-center gap-1 px-3 py-2 text-xs font-medium border-b-2 transition-colors',
                filter === tab.key
                  ? tab.activeColor
                  : 'border-transparent text-gray-500 hover:text-gray-700',
              )}
            >
              {tab.label}
              <span className={clsx(
                'rounded-full px-1.5 py-0.5 text-xs font-semibold',
                filter === tab.key && tab.key === 'overdue'  ? 'bg-rose-100 text-rose-700'
                : filter === tab.key && tab.key === 'due_soon' ? 'bg-amber-100 text-amber-700'
                : 'bg-gray-100 text-gray-600',
              )}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-gray-400">
          Нет разделов в этой категории
        </div>
      ) : (
        <>
          {/* Десктоп: таблица */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50/60">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide w-10"></th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Проект / Раздел</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Срок</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">ГИП</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Исполнитель</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Стадия</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide w-32">Выполнение</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {visible.map((item) => (
                  <AttentionRow key={item.section_id} item={item} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Мобильный: карточки */}
          <ul className="md:hidden divide-y divide-gray-50">
            {visible.map((item) => (
              <AttentionCard key={item.section_id} item={item} />
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

function AttentionRow({ item }: { item: AttentionItem }) {
  const end = new Date(item.planned_end)
  const daysLeft = differenceInCalendarDays(end, new Date())
  const rowCls = (item.is_overdue
    ? 'bg-rose-50/50 hover:bg-rose-50 transition-colors'
    : 'hover:bg-amber-50/40 transition-colors') + (item.is_mine === false ? ' opacity-60' : '')
  return (
    <tr className={rowCls} title={item.is_mine === false ? 'Чужой проект: только просмотр' : undefined}>
      <td className="px-4 py-2.5">
        <StatusDot isOverdue={item.is_overdue} />
      </td>
      <td className="px-4 py-2.5">
        <Link
          to={`/projects/${item.project_id}`}
          className="font-medium text-gray-900 hover:text-brand-700 transition-colors"
        >
          <span className="font-mono text-xs text-gray-500">{item.project_code}</span>
          {' '}
          <span
            className={clsx(
              'inline-block rounded px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide mr-0.5',
              item.is_overdue
                ? 'bg-rose-100 text-rose-700'
                : 'bg-amber-100 text-amber-700',
            )}
          >
            {DOC_TYPE_LABELS[item.doc_type]}
          </span>
          <span className="font-semibold">{item.section_code}</span>
        </Link>
        <div className="text-xs text-gray-500 truncate max-w-xs">{item.project_name}</div>
      </td>
      <td className="px-4 py-2.5 whitespace-nowrap">
        <DeadlineLabel end={end} daysLeft={daysLeft} isOverdue={item.is_overdue} />
      </td>
      <td className="px-4 py-2.5 text-gray-700 text-xs">{item.chief_name ?? '-'}</td>
      <td className="px-4 py-2.5 text-xs">
        {item.assignee_name
          ? <span className={clsx('font-medium', item.is_overdue ? 'text-rose-700' : 'text-amber-700')}>{item.assignee_name}</span>
          : <span className="text-gray-400 italic">не назначен</span>
        }
      </td>
      <td className="px-4 py-2.5 text-xs text-gray-700">{STATUS_LABEL[item.status]}</td>
      <td className="px-4 py-2.5">
        <ProgressBar value={item.progress} isOverdue={item.is_overdue} />
      </td>
    </tr>
  )
}

function AttentionCard({ item }: { item: AttentionItem }) {
  const end = new Date(item.planned_end)
  const daysLeft = differenceInCalendarDays(end, new Date())
  const cardCls = (item.is_overdue ? 'bg-rose-50/40' : '') + (item.is_mine === false ? ' opacity-60' : '')
  return (
    <li className={cardCls}>
      <Link to={`/projects/${item.project_id}`} className="block px-4 py-3 hover:bg-gray-50">
        <div className="flex items-start gap-2">
          <StatusDot isOverdue={item.is_overdue} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5 flex-wrap">
              <span className="font-mono text-xs text-gray-500">{item.project_code}</span>
              <span
                className={clsx(
                  'inline-block rounded px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                  item.is_overdue ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700',
                )}
              >
                {DOC_TYPE_LABELS[item.doc_type]}
              </span>
              <span>{item.section_code}</span>
            </div>
            <div className="text-xs text-gray-500 truncate">{item.project_name}</div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs mt-1">
              <span className="text-gray-600">ГИП: {item.chief_name ?? '-'}</span>
              <span className={item.is_overdue ? 'text-rose-700 font-medium' : 'text-amber-700 font-medium'}>
                Исп: {item.assignee_name ?? 'не назначен'}
              </span>
              <span className="text-gray-500">{STATUS_LABEL[item.status]}</span>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <DeadlineLabel end={end} daysLeft={daysLeft} isOverdue={item.is_overdue} />
              <div className="flex-1">
                <ProgressBar value={item.progress} isOverdue={item.is_overdue} />
              </div>
            </div>
          </div>
        </div>
      </Link>
    </li>
  )
}

function StatusDot({ isOverdue }: { isOverdue: boolean }) {
  return (
    <span
      aria-label={isOverdue ? 'Просрочен' : 'Скоро срок'}
      className={clsx(
        'inline-block h-2.5 w-2.5 rounded-full',
        isOverdue ? 'bg-rose-500' : 'bg-amber-400',
      )}
    />
  )
}

function DeadlineLabel({
  end,
  daysLeft,
  isOverdue,
}: {
  end: Date
  daysLeft: number
  isOverdue: boolean
}) {
  const label = isOverdue
    ? `просрочка ${Math.abs(daysLeft)} дн.`
    : daysLeft === 0
      ? 'сегодня'
      : `через ${daysLeft} дн.`
  return (
    <div className={clsx('text-xs', isOverdue ? 'text-rose-600' : 'text-amber-700')}>
      <div className="font-medium tabular-nums">{format(end, 'd MMM', { locale: ru })}</div>
      <div className="opacity-70">{label}</div>
    </div>
  )
}

function ProgressBar({ value, isOverdue }: { value: number; isOverdue?: boolean }) {
  const clamped = Math.max(0, Math.min(100, value))
  const color = isOverdue
    ? 'bg-rose-400'
    : clamped >= 90 ? 'bg-emerald-500'
    : clamped >= 40 ? 'bg-amber-400'
    : 'bg-gray-400'
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div className={clsx('h-full transition-all', color)} style={{ width: `${clamped}%` }} />
      </div>
      <span className={clsx('text-xs tabular-nums w-9 text-right', isOverdue ? 'text-rose-600 font-medium' : 'text-gray-600')}>
        {clamped}%
      </span>
    </div>
  )
}
